import { Mark, Node } from '@tiptap/react';
import { embedProvider, safeHref, safeImageSrc, wikiAssetId } from '../../markdown/wikiMarkdown';

/**
 * The Wiki editor schema — TipTap is ONLY the editing surface; the document
 * model is the prototype's flat block grammar (C1-editor.md §1.19–1.21) and
 * every node here parses and renders exactly the editor-HTML contract of
 * `mdToHtml` / `htmlToMd` (notes/FE-markdown.md):
 *
 *   <h1|h2|h3>inline</h…>            heading (h4–h6 parse as h3)
 *   <p>inline</p>                     paragraph (<br> = hardBreak)
 *   <ul|ol><li>inline</li></ul>       lists — items are TEXTBLOCKS, so nesting is impossible
 *   <ul data-task="1"><li data-checked="true|false">inline</li></ul>   checklist
 *   <blockquote [data-callout="note|warn"]>inline</blockquote>          quote / callout (inline, no <p>)
 *   <pre>text</pre>                   code block (no language, no marks)
 *   <hr>                              divider
 *   <table><thead><tr><th>text</th></tr></thead><tbody><tr><td>text</td></tr></tbody></table>   plain-text cells
 *   <p><img src alt></p>              image (block atom; only safe src)
 *   <div data-embed="url" contenteditable="false">▶ … video · url</div>   video (atom)
 *   marks: <b> <i> <s> <code> <a href>   — NO underline (the prototype blocks Ctrl/Cmd+U)
 *
 * Node names follow TipTap's conventions so the shared helpers read naturally.
 */

const PH_SKIP_CONTEXT = 'listItem/|taskItem/|blockquote/|tableCell/|tableHeader/|heading/';

/**
 * An attribute whose value only ever comes from the parse rule's getAttrs.
 * (TipTap otherwise also reads `el.getAttribute(<attr name>)` and coerces
 * numeric/boolean-looking strings, which would turn `alt="2024"` into a number.)
 */
const ruleAttr = (defaultValue, extra = {}) => ({ default: defaultValue, rendered: false, parseHTML: () => null, ...extra });

/** True when `el` is the last meaningful child of its parent (a contenteditable placeholder <br>). */
function isTrailingBreak(el) {
    let next = el.nextSibling;
    while (next && next.nodeType === 3 && !next.nodeValue) next = next.nextSibling;
    return !next;
}

/** `<p>` whose only content is one `<img>` → the image block (`<p><img></p>` contract shape). */
function imageOnlyParagraph(el) {
    const kids = [...el.childNodes].filter((n) => !(n.nodeType === 3 && !n.nodeValue.trim()));
    if (kids.length !== 1 || kids[0].nodeName !== 'IMG') return false;
    const src = kids[0].getAttribute('src') || '';
    if (!safeImageSrc(src)) return false;
    return { src, alt: kids[0].getAttribute('alt') || '' };
}

/** The atom's label, as text (embedLabel() returns escaped HTML). */
export function embedLabelText(url) {
    return `▶ ${embedProvider(url) || 'Video'} video · ${url}`;
}

export const WikiDoc = Node.create({
    name: 'doc',
    topNode: true,
    content: 'block+',
});

export const WikiText = Node.create({
    name: 'text',
    group: 'inline',
});

export const WikiParagraph = Node.create({
    name: 'paragraph',
    // First block type in the schema: ProseMirror's splitBlock / createParagraphNear
    // pick the first textblock of the `block` group as the default block.
    priority: 1000,
    group: 'block',
    content: 'inline*',
    parseHTML() {
        return [
            // TipTap / pasted shapes: <li><p>…</p></li>, <blockquote><p>…</p></blockquote>,
            // <th><p>…</p></th> — keep the inline content, drop the wrapper.
            { tag: 'p', context: PH_SKIP_CONTEXT, skip: true, priority: 70 },
            { tag: 'p' },
        ];
    },
    renderHTML() {
        return ['p', 0];
    },
});

export const WikiHeading = Node.create({
    name: 'heading',
    group: 'block',
    content: 'inline*',
    defining: true,
    addAttributes() {
        return { level: ruleAttr(1) };
    },
    parseHTML() {
        return [1, 2, 3, 4, 5, 6].map((n) => ({ tag: `h${n}`, attrs: { level: Math.min(3, n) } }));
    },
    renderHTML({ node }) {
        return [`h${Math.min(3, Math.max(1, node.attrs.level || 1))}`, 0];
    },
});

export const WikiBlockquote = Node.create({
    name: 'blockquote',
    group: 'block',
    content: 'inline*',
    defining: true,
    addAttributes() {
        return { callout: ruleAttr(null) };
    },
    parseHTML() {
        return [{
            tag: 'blockquote',
            getAttrs: (el) => {
                const c = el.getAttribute('data-callout');
                return { callout: c === 'note' || c === 'warn' ? c : null };
            },
        }];
    },
    renderHTML({ node }) {
        return node.attrs.callout ? ['blockquote', { 'data-callout': node.attrs.callout }, 0] : ['blockquote', 0];
    },
});

export const WikiCodeBlock = Node.create({
    name: 'codeBlock',
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    defining: true,
    parseHTML() {
        return [{ tag: 'pre', preserveWhitespace: 'full' }];
    },
    renderHTML() {
        return ['pre', 0];
    },
});

export const WikiHorizontalRule = Node.create({
    name: 'horizontalRule',
    group: 'block',
    atom: true,
    parseHTML() {
        return [{ tag: 'hr' }];
    },
    renderHTML() {
        return ['hr'];
    },
});

const isTaskUl = (el) => el.getAttribute('data-task') === '1' || el.getAttribute('data-type') === 'taskList';

export const WikiBulletList = Node.create({
    name: 'bulletList',
    group: 'block',
    content: 'listItem+',
    parseHTML() {
        return [{ tag: 'ul', getAttrs: (el) => (isTaskUl(el) ? false : null) }];
    },
    renderHTML() {
        return ['ul', 0];
    },
});

export const WikiOrderedList = Node.create({
    name: 'orderedList',
    group: 'block',
    content: 'listItem+',
    parseHTML() {
        return [{ tag: 'ol' }];
    },
    renderHTML() {
        // Numbering is never stored (the prototype renumbers from 1).
        return ['ol', 0];
    },
});

export const WikiListItem = Node.create({
    name: 'listItem',
    content: 'inline*',
    defining: true,
    parseHTML() {
        return [{ tag: 'li' }];
    },
    renderHTML() {
        return ['li', 0];
    },
});

export const WikiTaskList = Node.create({
    name: 'taskList',
    group: 'block',
    content: 'taskItem+',
    parseHTML() {
        return [{ tag: 'ul', priority: 60, getAttrs: (el) => (isTaskUl(el) ? null : false) }];
    },
    renderHTML() {
        return ['ul', { 'data-task': '1' }, 0];
    },
});

export const WikiTaskItem = Node.create({
    name: 'taskItem',
    content: 'inline*',
    defining: true,
    addAttributes() {
        // A new item made by Enter is always unchecked (prototype), so the flag is
        // not copied by TipTap's splitBlock.
        return { checked: ruleAttr(false, { keepOnSplit: false }) };
    },
    parseHTML() {
        return [{
            tag: 'li',
            context: 'taskList/',
            priority: 60,
            getAttrs: (el) => ({ checked: el.getAttribute('data-checked') === 'true' }),
        }];
    },
    renderHTML({ node }) {
        return ['li', { 'data-checked': node.attrs.checked ? 'true' : 'false' }, 0];
    },
});

export const WikiTable = Node.create({
    name: 'table',
    group: 'block',
    content: 'tableHead tableBody',
    isolating: true,
    parseHTML() {
        return [{ tag: 'table' }];
    },
    renderHTML() {
        return ['table', 0];
    },
});

export const WikiTableHead = Node.create({
    name: 'tableHead',
    content: 'tableRow',
    isolating: true,
    parseHTML() {
        return [{ tag: 'thead' }];
    },
    renderHTML() {
        return ['thead', 0];
    },
});

export const WikiTableBody = Node.create({
    name: 'tableBody',
    content: 'tableRow*',
    isolating: true,
    parseHTML() {
        return [{ tag: 'tbody' }];
    },
    renderHTML() {
        return ['tbody', 0];
    },
});

export const WikiTableRow = Node.create({
    name: 'tableRow',
    content: '(tableHeader | tableCell)+',
    parseHTML() {
        return [{ tag: 'tr' }];
    },
    renderHTML() {
        return ['tr', 0];
    },
});

export const WikiTableHeader = Node.create({
    name: 'tableHeader',
    content: 'text*',
    marks: '',
    isolating: true,
    parseHTML() {
        return [{ tag: 'th' }];
    },
    renderHTML() {
        return ['th', 0];
    },
});

export const WikiTableCell = Node.create({
    name: 'tableCell',
    content: 'text*',
    marks: '',
    isolating: true,
    parseHTML() {
        return [{ tag: 'td' }];
    },
    renderHTML() {
        return ['td', 0];
    },
});

export const WikiImage = Node.create({
    name: 'image',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: false,
    addOptions() {
        // (assetId) => Promise<url|null>. `/api/wiki/assets/<id>` needs the bearer
        // token, so the editor shows a resolved presigned URL while the stored
        // `src` stays the stable asset path.
        return { resolveAsset: null };
    },
    addAttributes() {
        return {
            src: ruleAttr(''),
            alt: ruleAttr(''),
        };
    },
    parseHTML() {
        return [
            { tag: 'p', priority: 80, getAttrs: (el) => imageOnlyParagraph(el) },
            {
                tag: 'img[src]',
                getAttrs: (el) => {
                    const src = el.getAttribute('src') || '';
                    return safeImageSrc(src) ? { src, alt: el.getAttribute('alt') || '' } : false;
                },
            },
        ];
    },
    renderHTML({ node }) {
        return ['p', ['img', { src: node.attrs.src, alt: node.attrs.alt || '' }]];
    },
    addNodeView() {
        const { resolveAsset } = this.options;
        return ({ node }) => {
            let current = node;
            let alive = true;
            const dom = document.createElement('p');
            dom.className = 'wk-ed-figure';
            const img = document.createElement('img');
            dom.appendChild(img);
            const paint = () => {
                img.setAttribute('alt', current.attrs.alt || '');
                const src = current.attrs.src || '';
                const id = wikiAssetId(src);
                if (id && typeof resolveAsset === 'function') {
                    img.setAttribute('data-asset', id);
                    img.removeAttribute('src');
                    Promise.resolve(resolveAsset(id)).then((url) => {
                        if (alive && url && current.attrs.src === src) img.setAttribute('src', url);
                    }).catch(() => {});
                } else if (safeImageSrc(src)) {
                    img.removeAttribute('data-asset');
                    img.setAttribute('src', src);
                }
            };
            paint();
            return {
                dom,
                update(next) {
                    if (next.type !== current.type) return false;
                    const changed = next.attrs.src !== current.attrs.src || next.attrs.alt !== current.attrs.alt;
                    current = next;
                    if (changed) paint();
                    return true;
                },
                destroy() {
                    alive = false;
                },
            };
        };
    },
});

export const WikiEmbed = Node.create({
    name: 'embed',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: false,
    addAttributes() {
        return { url: ruleAttr('') };
    },
    parseHTML() {
        return [{ tag: 'div[data-embed]', getAttrs: (el) => ({ url: el.getAttribute('data-embed') || '' }) }];
    },
    renderHTML({ node }) {
        return ['div', { 'data-embed': node.attrs.url, contenteditable: 'false' }, embedLabelText(node.attrs.url)];
    },
});

export const WikiHardBreak = Node.create({
    name: 'hardBreak',
    group: 'inline',
    inline: true,
    selectable: false,
    parseHTML() {
        return [
            // The contenteditable placeholder <br> of an empty block (`<p><br></p>`,
            // `<td><br></td>`, `<pre><br></pre>`) is not content.
            { tag: 'br', priority: 60, ignore: true, getAttrs: (el) => (isTrailingBreak(el) ? null : false) },
            { tag: 'br' },
        ];
    },
    renderHTML() {
        return ['br'];
    },
});

export const WikiLink = Mark.create({
    name: 'link',
    priority: 1000,
    inclusive: false,
    addAttributes() {
        return { href: ruleAttr(null) };
    },
    parseHTML() {
        return [{
            tag: 'a[href]',
            getAttrs: (el) => {
                const href = el.getAttribute('href') || '';
                return safeHref(href) ? { href } : false;
            },
        }];
    },
    renderHTML({ mark }) {
        return ['a', { href: mark.attrs.href }, 0];
    },
});

export const WikiBold = Mark.create({
    name: 'bold',
    parseHTML() {
        return [{ tag: 'b' }, { tag: 'strong' }];
    },
    renderHTML() {
        return ['b', 0];
    },
});

export const WikiItalic = Mark.create({
    name: 'italic',
    parseHTML() {
        return [{ tag: 'i' }, { tag: 'em' }];
    },
    renderHTML() {
        return ['i', 0];
    },
});

export const WikiStrike = Mark.create({
    name: 'strike',
    parseHTML() {
        return [{ tag: 's' }, { tag: 'strike' }, { tag: 'del' }];
    },
    renderHTML() {
        return ['s', 0];
    },
});

export const WikiCode = Mark.create({
    name: 'code',
    excludes: '_',
    code: true,
    // Typing after inline code is plain text (the prototype appended a ZWSP).
    inclusive: false,
    parseHTML() {
        return [{ tag: 'code' }];
    },
    renderHTML() {
        return ['code', 0];
    },
});

export const WIKI_SCHEMA_EXTENSIONS = [
    WikiDoc, WikiText, WikiParagraph, WikiHeading, WikiBlockquote, WikiCodeBlock, WikiHorizontalRule,
    WikiBulletList, WikiOrderedList, WikiListItem, WikiTaskList, WikiTaskItem,
    WikiTable, WikiTableHead, WikiTableBody, WikiTableRow, WikiTableHeader, WikiTableCell,
    WikiImage, WikiEmbed, WikiHardBreak,
    WikiLink, WikiBold, WikiItalic, WikiStrike, WikiCode,
];

/** Node-type helpers shared by the commands, keymap and plugins. */
export const LIST_NODE_BY_KIND = Object.freeze({ ul: 'bulletList', ol: 'orderedList', task: 'taskList' });
export const LIST_KIND_BY_NODE = Object.freeze({ bulletList: 'ul', orderedList: 'ol', taskList: 'task' });
export const isCellNode = (node) => !!node && (node.type.name === 'tableCell' || node.type.name === 'tableHeader');
export const isItemNode = (node) => !!node && (node.type.name === 'listItem' || node.type.name === 'taskItem');
